import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { Modal } from '../components/common/Modal';
import { Pagination, usePaginationState } from '../components/common/Pagination';
import {
  Plus, Key, RotateCw, ShieldOff, Shield, Pause, Play, Copy,
  AlertTriangle, Clock, Eye, EyeOff, FileText, Trash2, Server,
  Activity, Lock, Unlock, ChevronDown, ChevronUp, Search,
} from 'lucide-react';
import PageHeader from '../components/common/PageHeader';
/* G102 — full v2 redesign of GatewayApiKeysPage. Replaces GovPageLayout
 * + PrimaryButton chrome with V2PageShell + V2Button. v1 hooks /
 * handlers / api calls preserved verbatim. */
import {
  V2PageShell,
  Button as V2Button,
  Pill as V2Pill,
} from '../components/v2';
import {
  BRAND, S, ACCENT, STATUS_MAP,
  GovStyles, GovPageLayout, StatCard, StatusBadge,
  Card, SearchInput, RefreshButton, PrimaryButton, GhostButton,
  EmptyState, ErrorBanner, LoadingSkeleton, Badge, SectionLabel,
  btnBase, inputBase,
} from '../components/common/GovernanceDesignSystem';
import * as api from '../services/api';

// ---------------------------------------------------------------------------
// Helper: unwrap apiFetch response
// ---------------------------------------------------------------------------
const unwrap = (res) => {
  if (res && typeof res === 'object' && 'data' in res) return res.data;
  return res;
};

// ---------------------------------------------------------------------------
// CONSTANTS
// ---------------------------------------------------------------------------
const KEY_TYPE_LABELS = {
  GATEWAY: 'Gateway (Full Proxy)',
  SDK: 'SDK (Split Governance)',
  // WORKER keys authenticate Conductor-style polling workers against
  // /api/tasks/poll/... and POST /api/tasks. Scoped to a list of
  // workflow ids; empty = tenant-wide. See backend GatewayApiKey
  // KEY_TYPE_WORKER + GatewayAuthenticationFilter for the full path.
  WORKER: 'Worker (Conductor SDK)',
};

// Status mapping for API key statuses
const STATUS_KEY_MAP = {
  ACTIVE: 'active',
  ROTATING: 'rotating',
  SUSPENDED: 'suspended',
  REVOKED: 'revoked',
  EXPIRED: 'expired',
};

const labelStyle = {
  display: 'block', fontSize: 12, fontWeight: 600, color: S[600], marginBottom: 8,
};

const selectStyle = {
  padding: '9px 12px', borderRadius: 8, border: `1px solid ${S[200]}`, background: '#ffffff',
  fontSize: 13, boxSizing: 'border-box', fontFamily: 'inherit', cursor: 'pointer',
  color: S[900], outline: 'none', transition: 'border-color 0.15s, box-shadow 0.15s',
};

const formGroupStyle = { marginBottom: 16 };
const formRowStyle = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 };

const toolbarStyle = { display: 'flex', gap: 12, alignItems: 'center', marginBottom: 20, flexWrap: 'wrap' };

const monoStyle = {
  fontFamily: "'IBM Plex Mono', monospace", fontSize: 12, background: S[100],
  padding: '4px 8px', borderRadius: 4, color: S[900], wordBreak: 'break-all',
};

const thStyle = {
  padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: S[500],
  textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: `1px solid ${S[200]}`, background: S[50],
};

const tdStyle = { padding: '12px 16px', fontSize: 13, borderBottom: `1px solid ${S[100]}` };

// ---------------------------------------------------------------------------
// Key Secret Display (one-time plaintext)
// ---------------------------------------------------------------------------
/**
 * Renders the one-time creation success state for a new API key. Shows
 * BOTH values the SDK needs:
 *   - keyId    — the UUID. SDKs put this in keyId / CONDUCTOR_AUTH_KEY.
 *                Persistent (visible in the keys list later) but still
 *                worth surfacing once for copy convenience.
 *   - keySecret — the plaintext agt_*_… value. Hashed in the DB; this
 *                 is the only opportunity to copy it. SDKs put this in
 *                 keySecret / CONDUCTOR_AUTH_SECRET.
 *
 * Each value gets its own copy button so a user can paste them
 * straight into env vars without splitting strings by hand.
 */
function KeySecretDisplay({ keyId, plaintext, onDismiss }) {
  const [copiedField, setCopiedField] = useState(null); // 'id' | 'secret' | null

  const copyValue = useCallback(async (value, field) => {
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      const el = document.createElement('textarea');
      el.value = value;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
    }
    setCopiedField(field);
    setTimeout(() => setCopiedField((f) => (f === field ? null : f)), 2000);
  }, []);

  const labelStyle = {
    fontSize: 11, fontWeight: 700, color: ACCENT.amber.text,
    letterSpacing: '0.04em', textTransform: 'uppercase',
    marginBottom: 6, marginTop: 12,
  };
  const valueRowStyle = {
    background: S[900], color: ACCENT.amber.border, padding: '10px 14px', borderRadius: 8,
    fontFamily: "'IBM Plex Mono', monospace", fontSize: 13,
    wordBreak: 'break-all', lineHeight: 1.6,
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
  };
  const copyBtnStyle = {
    ...btnBase, padding: '4px 10px', fontSize: 11, fontWeight: 600,
    background: ACCENT.amber.icon, color: '#fff', borderRadius: 6, flexShrink: 0,
  };

  return (
    <div style={{
      background: ACCENT.amber.bg, border: `2px solid ${ACCENT.amber.icon}`, borderRadius: 12, padding: 20, marginBottom: 20,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <AlertTriangle size={18} color={ACCENT.amber.icon} />
        <span style={{ fontSize: 14, fontWeight: 700, color: ACCENT.amber.text }}>
          Save these values now — the secret will not be shown again
        </span>
      </div>

      {keyId && (
        <>
          <div style={labelStyle}>Key ID  ·  set as <code>CONDUCTOR_AUTH_KEY</code> / <code>keyId</code> in the SDK</div>
          <div style={valueRowStyle}>
            <span>{keyId}</span>
            <button onClick={() => copyValue(keyId, 'id')} style={copyBtnStyle}>
              <Copy size={11} /> {copiedField === 'id' ? 'Copied' : 'Copy'}
            </button>
          </div>
        </>
      )}

      <div style={labelStyle}>Key Secret  ·  set as <code>CONDUCTOR_AUTH_SECRET</code> / <code>keySecret</code> in the SDK</div>
      <div style={valueRowStyle}>
        <span>{plaintext}</span>
        <button onClick={() => copyValue(plaintext, 'secret')} style={copyBtnStyle}>
          <Copy size={11} /> {copiedField === 'secret' ? 'Copied' : 'Copy'}
        </button>
      </div>

      <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
        <button onClick={onDismiss} style={{
          ...btnBase, padding: '8px 14px', fontSize: 12, fontWeight: 600,
          background: 'transparent', color: S[600], border: `1px solid ${S[200]}`, borderRadius: 8,
        }}>
          I've saved both — dismiss
        </button>
      </div>
    </div>
  );
}

/**
 * Searchable, multi-select picker that takes a list of `{id, label,
 * sublabel}` items and an array of currently-selected ids, and emits
 * the new array of ids on every change. Used by the Create Key modal
 * to replace the two raw-UUID textareas with name-driven selection
 * (the user picks "Phishing Triage" — we store its UUID under the
 * hood). Selected items show as removable chips above the search box;
 * candidate items render as a checkbox list filtered by the search
 * input.
 *
 * Loading / empty / error states are handled by the caller — this
 * component just renders whatever items it's given.
 */
function MultiSelectPicker({
  items,
  selectedIds,
  onChange,
  placeholder = 'Search…',
  emptyHint = 'No items available',
  maxHeight = 220,
}) {
  const [search, setSearch] = useState('');
  const safeSelected = Array.isArray(selectedIds) ? selectedIds : [];
  const lookup = useMemo(() => {
    const m = new Map();
    (items || []).forEach((it) => { if (it && it.id) m.set(it.id, it); });
    return m;
  }, [items]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items || [];
    return (items || []).filter((it) => {
      if (!it) return false;
      return (
        (it.label || '').toLowerCase().includes(q) ||
        (it.sublabel || '').toLowerCase().includes(q) ||
        (it.id || '').toLowerCase().includes(q)
      );
    });
  }, [items, search]);

  const toggle = (id) => {
    const has = safeSelected.includes(id);
    onChange(has ? safeSelected.filter((x) => x !== id) : [...safeSelected, id]);
  };

  return (
    <div>
      {/* Selected chips — above the picker so they're always visible. */}
      {safeSelected.length > 0 && (
        <div style={{
          display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8,
          padding: 8, background: '#FAFAF9',
          border: '1px solid #E7E5E4', borderRadius: 8,
        }}>
          {safeSelected.map((id) => {
            const it = lookup.get(id);
            return (
              <span key={id} style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '4px 8px', background: '#fff',
                border: '1px solid #D6D3D1', borderRadius: 999,
                fontSize: 11, fontWeight: 500, color: '#1C1917',
              }}>
                {it ? it.label : id}
                {!it && (
                  <span style={{
                    fontFamily: "'IBM Plex Mono', monospace", fontSize: 10,
                    color: '#A8A29E',
                  }}>(unknown)</span>
                )}
                <button
                  type="button"
                  onClick={() => toggle(id)}
                  title="Remove"
                  style={{
                    border: 'none', background: 'transparent',
                    cursor: 'pointer', color: '#78716C', padding: 0,
                    fontSize: 14, lineHeight: 1,
                  }}
                >×</button>
              </span>
            );
          })}
        </div>
      )}

      {/* Search box */}
      <input
        type="text"
        placeholder={placeholder}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        style={{
          width: '100%', padding: '8px 10px', fontSize: 12,
          border: '1px solid #E7E5E4', borderRadius: 6, outline: 'none',
          fontFamily: 'inherit', boxSizing: 'border-box',
        }}
      />

      {/* Candidate list — checkboxes for toggling. */}
      <div style={{
        marginTop: 6, maxHeight, overflowY: 'auto',
        border: '1px solid #E7E5E4', borderRadius: 6,
        background: '#fff',
      }}>
        {filtered.length === 0 ? (
          <div style={{ padding: '12px 14px', fontSize: 11, color: '#A8A29E' }}>
            {(items || []).length === 0 ? emptyHint : 'No matches for that search.'}
          </div>
        ) : (
          filtered.map((it) => {
            const checked = safeSelected.includes(it.id);
            return (
              <label
                key={it.id}
                style={{
                  display: 'flex', alignItems: 'flex-start', gap: 8,
                  padding: '6px 10px', cursor: 'pointer',
                  borderBottom: '1px solid #F5F5F4',
                  background: checked ? '#FEF7F2' : 'transparent',
                }}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggle(it.id)}
                  style={{ marginTop: 2, cursor: 'pointer' }}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontSize: 12, fontWeight: 500, color: '#1C1917',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {it.label}
                  </div>
                  {it.sublabel && (
                    <div style={{
                      fontSize: 10, color: '#78716C', marginTop: 1,
                      fontFamily: "'IBM Plex Mono', monospace",
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      {it.sublabel}
                    </div>
                  )}
                </div>
              </label>
            );
          })
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// MAIN COMPONENT
// ---------------------------------------------------------------------------
export default function GatewayApiKeysPage() {
  const { user } = useAuth();
  const { addToast } = useToast();

  // Data
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  // Tenant agents + workflows for the scope pickers. Loaded lazily on
  // create-modal open so we don't burn API calls on every page nav.
  const [agentList, setAgentList] = useState([]);
  const [workflowList, setWorkflowList] = useState([]);

  // Modals
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isRotateModalOpen, setIsRotateModalOpen] = useState(false);
  const [isRevokeModalOpen, setIsRevokeModalOpen] = useState(false);
  const [isSuspendModalOpen, setIsSuspendModalOpen] = useState(false);
  const [isAuditModalOpen, setIsAuditModalOpen] = useState(false);
  const [isScopeModalOpen, setIsScopeModalOpen] = useState(false);

  // Target key for actions
  const [targetKey, setTargetKey] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);

  // Created key — both keyId (UUID) and plaintext are surfaced once
  // because the SDK auth flow needs the pair.
  const [newKeyPlaintext, setNewKeyPlaintext] = useState(null);
  const [newKeyId, setNewKeyId] = useState(null);

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterType, setFilterType] = useState('');
  const { page, pageSize, setPage } = usePaginationState(20);

  // Form state — Create. scopedAgentIds and scopedWorkflowIds are now
  // string-arrays (UUIDs), driven by the MultiSelectPicker chips
  // rather than free-form textareas. The change is local to this
  // component; the wire payload still sends string-arrays so the
  // server contract is unchanged.
  const CREATE_DEFAULTS = {
    name: '',
    description: '',
    keyType: 'GATEWAY',
    expiresInDays: 90,
    rateLimit: 60,
    burstLimit: 10,
    ipAllowlist: '',
    scopedAgentIds: [],
    // WORKER keys only — list of workflow UUIDs this key is
    // permissioned for. Empty array = tenant-wide.
    scopedWorkflowIds: [],
    permissions: 'EXECUTE,READ',
  };
  const [createForm, setCreateForm] = useState(CREATE_DEFAULTS);
  const [createFormErrors, setCreateFormErrors] = useState({});

  // Form state — Rotate
  const [rotateGraceHours, setRotateGraceHours] = useState(24);

  // Form state — Revoke / Suspend
  const [actionReason, setActionReason] = useState('');

  // Form state — Update Scope
  const [scopeForm, setScopeForm] = useState('');
  const [scopeFormErrors, setScopeFormErrors] = useState({});

  // ── Data Loading ─────────────────────────────────────────────────────────

  const loadKeys = useCallback(async () => {
    try {
      setLoading(true);
      const data = unwrap(await api.gatewayListKeys(filterStatus || undefined));
      setKeys(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error loading gateway keys:', err);
      addToast('Failed to load API keys', 'error');
      setKeys([]);
    } finally {
      setLoading(false);
    }
  }, [filterStatus, addToast]);

  useEffect(() => { loadKeys(); }, [loadKeys]);

  // ── Computed ──────────────────────────────────────────────────────────────

  const filteredKeys = useMemo(() => {
    return keys.filter((k) => {
      const matchesSearch = !searchTerm
        || k.name?.toLowerCase().includes(searchTerm.toLowerCase())
        || k.description?.toLowerCase().includes(searchTerm.toLowerCase())
        || k.keyPrefix?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesType = !filterType || k.keyType === filterType;
      return matchesSearch && matchesType;
    });
  }, [keys, searchTerm, filterType]);

  const paginatedKeys = useMemo(() => {
    const start = page * pageSize;
    return filteredKeys.slice(start, start + pageSize);
  }, [filteredKeys, page, pageSize]);

  const stats = useMemo(() => ({
    total: keys.length,
    active: keys.filter((k) => k.status === 'ACTIVE').length,
    rotating: keys.filter((k) => k.status === 'ROTATING').length,
    suspended: keys.filter((k) => k.status === 'SUSPENDED').length,
    revoked: keys.filter((k) => k.status === 'REVOKED' || k.status === 'EXPIRED').length,
  }), [keys]);

  // ── Handlers ──────────────────────────────────────────────────────────────

  // UUID format: 8-4-4-4-12 hex chars
  const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

  const handleCreate = useCallback(async () => {
    // ── Client-side validation ──────────────────────────────────────────
    const errors = {};

    if (!createForm.name.trim()) {
      errors.name = 'Key name is required.';
    }

    // The picker stores UUIDs in arrays. Defensive UUID validation in
    // case the form was migrated from older shapes — picker entries
    // are always valid UUIDs, but we guard against stale state.
    const agentIds = Array.isArray(createForm.scopedAgentIds)
      ? createForm.scopedAgentIds.filter(Boolean)
      : [];
    const badAgent = agentIds.filter((id) => !UUID_RE.test(id));
    if (badAgent.length > 0) {
      errors.scopedAgentIds = `Invalid agent UUID: "${badAgent[0]}".`;
    }

    const workflowIds = Array.isArray(createForm.scopedWorkflowIds)
      ? createForm.scopedWorkflowIds.filter(Boolean)
      : [];
    const badWorkflow = workflowIds.filter((id) => !UUID_RE.test(id));
    if (badWorkflow.length > 0) {
      errors.scopedWorkflowIds = `Invalid workflow UUID: "${badWorkflow[0]}".`;
    }

    if (Object.keys(errors).length > 0) {
      setCreateFormErrors(errors);
      return; // Stay on the form — don't close anything
    }
    setCreateFormErrors({});

    // ── Build payload ───────────────────────────────────────────────────
    try {
      const expiresInDays = parseInt(createForm.expiresInDays, 10) || 90;
      const expiresAt = new Date(Date.now() + expiresInDays * 86400000).toISOString().slice(0, 19);
      const payload = {
        name: createForm.name.trim(),
        description: createForm.description.trim(),
        keyType: createForm.keyType,
        expiresAt,
        rateLimitRpm: parseInt(createForm.rateLimit, 10) || 60,
        rateLimitBurst: parseInt(createForm.burstLimit, 10) || 10,
        permissions: createForm.permissions
          .split(',')
          .map((p) => p.trim())
          .filter(Boolean),
      };

      // Parse IP allowlist
      const ipRaw = createForm.ipAllowlist.trim();
      if (ipRaw) {
        payload.ipAllowlist = ipRaw.split('\n').map((l) => l.trim()).filter(Boolean);
      }

      // Wire shapes are unchanged: server still expects string-arrays
      // of UUIDs. The pickers already store UUIDs directly.
      if (agentIds.length > 0) {
        payload.scopedAgentIds = agentIds;
      }
      if (workflowIds.length > 0) {
        payload.scopedWorkflowIds = workflowIds;
      }

      const result = unwrap(await api.gatewayCreateKey(payload));
      if (result?.plaintextKey) {
        setNewKeyPlaintext(result.plaintextKey);
        // The keyId is the UUID Conductor SDKs put in keyId /
        // CONDUCTOR_AUTH_KEY. Backend serialises CreateKeyResponse as
        // { key: <full GatewayApiKey>, plaintextKey } so we pull
        // result.key.id. Defensive about the shape — older clients of
        // this page may not have the key embedded.
        if (result?.key?.id) {
          setNewKeyId(result.key.id);
        } else if (result?.id) {
          // Some unwrap paths flatten; tolerate both shapes.
          setNewKeyId(result.id);
        }
      }
      addToast(`API key "${payload.name}" created successfully`, 'success');
      setIsCreateModalOpen(false);
      setCreateForm(CREATE_DEFAULTS);
      setCreateFormErrors({});
      loadKeys();
    } catch (err) {
      console.error('Error creating key:', err);
      // Parse field-level errors from backend response
      const serverField = err?.field;
      const serverMsg = err?.message || 'Failed to create API key';
      if (serverField) {
        setCreateFormErrors({ [serverField]: serverMsg });
      } else {
        addToast(serverMsg, 'error');
      }
      // Form stays open with all values intact — user can correct and retry
    }
  }, [createForm, addToast, loadKeys]);

  const handleRotate = useCallback(async () => {
    if (!targetKey) return;
    try {
      await api.gatewayRotateKey(targetKey.id, parseInt(rotateGraceHours, 10) || 24);
      addToast(`Key "${targetKey.name}" rotation initiated`, 'success');
      setIsRotateModalOpen(false);
      setTargetKey(null);
      loadKeys();
    } catch (err) {
      addToast(err?.message || 'Failed to rotate key', 'error');
    }
  }, [targetKey, rotateGraceHours, addToast, loadKeys]);

  const handleRevoke = useCallback(async () => {
    if (!targetKey) return;
    try {
      await api.gatewayRevokeKey(targetKey.id, actionReason || 'Manual revocation');
      addToast(`Key "${targetKey.name}" revoked`, 'success');
      setIsRevokeModalOpen(false);
      setTargetKey(null);
      setActionReason('');
      loadKeys();
    } catch (err) {
      addToast(err?.message || 'Failed to revoke key', 'error');
    }
  }, [targetKey, actionReason, addToast, loadKeys]);

  const handleSuspend = useCallback(async () => {
    if (!targetKey) return;
    try {
      await api.gatewaySuspendKey(targetKey.id, actionReason || 'Manual suspension');
      addToast(`Key "${targetKey.name}" suspended`, 'success');
      setIsSuspendModalOpen(false);
      setTargetKey(null);
      setActionReason('');
      loadKeys();
    } catch (err) {
      addToast(err?.message || 'Failed to suspend key', 'error');
    }
  }, [targetKey, actionReason, addToast, loadKeys]);

  const handleReactivate = useCallback(async (key) => {
    try {
      await api.gatewayReactivateKey(key.id);
      addToast(`Key "${key.name}" reactivated`, 'success');
      loadKeys();
    } catch (err) {
      addToast(err?.message || 'Failed to reactivate key', 'error');
    }
  }, [addToast, loadKeys]);

  const handleUpdateScope = useCallback(async () => {
    if (!targetKey) return;
    setScopeFormErrors({});
    try {
      const ids = scopeForm.trim()
        ? scopeForm.split('\n').map((l) => l.trim()).filter(Boolean)
        : [];

      // Client-side UUID validation
      if (ids.length > 0) {
        const badIds = ids.filter((id) => !UUID_RE.test(id));
        if (badIds.length > 0) {
          setScopeFormErrors({
            scopedAgentIds: `Invalid UUID(s): ${badIds.join(', ')}. Use the Agent UUID from Agent Details (not the External Agent ID).`,
          });
          return;
        }
      }

      await api.gatewayUpdateKeyScope(targetKey.id, ids);
      addToast(`Scope updated for "${targetKey.name}"`, 'success');
      setIsScopeModalOpen(false);
      setTargetKey(null);
      loadKeys();
    } catch (err) {
      if (err?.field) {
        setScopeFormErrors({ [err.field]: err.message });
      } else {
        addToast(err?.message || 'Failed to update scope', 'error');
      }
    }
  }, [targetKey, scopeForm, addToast, loadKeys]);

  const handleViewAudit = useCallback(async (key) => {
    try {
      setTargetKey(key);
      setAuditLoading(true);
      setIsAuditModalOpen(true);
      const data = unwrap(await api.gatewayGetKeyAudit(key.id));
      setAuditLogs(Array.isArray(data) ? data : []);
    } catch (err) {
      addToast('Failed to load audit log', 'error');
      setAuditLogs([]);
    } finally {
      setAuditLoading(false);
    }
  }, [addToast]);

  // ── Action openers ────────────────────────────────────────────────────────

  const openRotate = (key) => { setTargetKey(key); setRotateGraceHours(24); setIsRotateModalOpen(true); };
  const openRevoke = (key) => { setTargetKey(key); setActionReason(''); setIsRevokeModalOpen(true); };
  const openSuspend = (key) => { setTargetKey(key); setActionReason(''); setIsSuspendModalOpen(true); };
  const openScope = (key) => {
    setTargetKey(key);
    // scopedAgentIds comes as a JSON array from backend — handle both array and null
    let existing = '';
    if (Array.isArray(key.scopedAgentIds)) {
      existing = key.scopedAgentIds.join('\n');
    } else if (key.scopedAgentIds && typeof key.scopedAgentIds === 'object') {
      // Handle Jackson JsonNode serialization (array-like objects)
      existing = Object.values(key.scopedAgentIds).join('\n');
    }
    setScopeForm(existing);
    setScopeFormErrors({});
    setIsScopeModalOpen(true);
  };

  // ── Formatting helpers ────────────────────────────────────────────────────

  const fmtDate = (d) => {
    if (!d) return '—';
    try {
      return new Date(d).toLocaleDateString('en-US', {
        year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
      });
    } catch {
      return String(d);
    }
  };

  const fmtNumber = (n) => {
    if (n == null) return '0';
    return Number(n).toLocaleString();
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════════════════

  /* G102 — open the Create API Key modal + lazy-load pickers.
   * Behaviour identical to v1 — just hoisted out of the JSX. */
  const handleOpenCreateApiKey = async () => {
    setCreateForm(CREATE_DEFAULTS);
    setCreateFormErrors({});
    setIsCreateModalOpen(true);
    (async () => {
      try {
        const ag = unwrap(await api.getAgents());
        if (Array.isArray(ag)) {
          setAgentList(ag.map((a) => ({ id: a.id, label: a.name || a.id, sublabel: a.id })));
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('Failed to load agents for picker', err);
      }
    })();
    (async () => {
      try {
        const wf = unwrap(await api.getWorkflows({}));
        const list = Array.isArray(wf) ? wf : (wf?.workflows || []);
        setWorkflowList(list.map((w) => ({
          id: w.id,
          label: w.name || w.id,
          sublabel: w.description ? `${w.description.slice(0, 60)}${w.description.length > 60 ? '…' : ''}` : w.id,
        })));
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('Failed to load workflows for picker', err);
      }
    })();
  };

  return (
    <V2PageShell
      title="Gateway · API Keys"
      subtitle="Issue, rotate, and revoke gateway API keys."
      tags={<V2Pill tone="cyan" size="sm">Gateway</V2Pill>}
      actions={
        <V2Button variant="primary" size="sm" iconLeft={<Plus size={14} />} onClick={handleOpenCreateApiKey}>
          Create API Key
        </V2Button>
      }
    >
      <GovStyles />

      {/* Created key banner (shown once) */}
      {newKeyPlaintext && (
        <KeySecretDisplay
          keyId={newKeyId}
          plaintext={newKeyPlaintext}
          onDismiss={() => { setNewKeyPlaintext(null); setNewKeyId(null); }}
        />
      )}

      {/* Stats */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 24, flexWrap: 'wrap' }}>
        <StatCard label="Total Keys" value={stats.total} accent={ACCENT.slate} />
        <StatCard label="Active" value={stats.active} accent={ACCENT.emerald} />
        <StatCard label="Rotating" value={stats.rotating} accent={ACCENT.violet} />
        <StatCard label="Suspended" value={stats.suspended} accent={ACCENT.rose} />
        <StatCard label="Revoked / Expired" value={stats.revoked} accent={ACCENT.slate} />
      </div>

      {/* Toolbar */}
      <div style={toolbarStyle}>
        <SearchInput
          value={searchTerm}
          onChange={(val) => { setSearchTerm(val); setPage(0); }}
          placeholder="Search by name, description, or prefix..."
          maxWidth={360}
        />
        <select style={selectStyle} value={filterStatus} onChange={(e) => { setFilterStatus(e.target.value); setPage(0); }}>
          <option value="">All Statuses</option>
          <option value="ACTIVE">Active</option>
          <option value="ROTATING">Rotating</option>
          <option value="SUSPENDED">Suspended</option>
          <option value="REVOKED">Revoked</option>
          <option value="EXPIRED">Expired</option>
        </select>
        <select style={selectStyle} value={filterType} onChange={(e) => { setFilterType(e.target.value); setPage(0); }}>
          <option value="">All Types</option>
          <option value="GATEWAY">Gateway</option>
          <option value="SDK">SDK</option>
          <option value="WORKER">Worker</option>
        </select>
        <RefreshButton onClick={loadKeys} />
      </div>

      {/* Keys Table */}
      <Card>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 40, color: S[500] }}>Loading API keys...</div>
        ) : paginatedKeys.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, color: S[500] }}>
            {keys.length === 0 ? 'No API keys created yet. Create your first key to get started.' : 'No keys match your filters.'}
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={thStyle}>Name</th>
                  <th style={thStyle}>Prefix</th>
                  <th style={thStyle}>Type</th>
                  <th style={thStyle}>Status</th>
                  <th style={thStyle}>Rate Limit</th>
                  <th style={thStyle}>Usage</th>
                  <th style={thStyle}>Expires</th>
                  <th style={thStyle}>Created</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedKeys.map((key) => (
                  <tr key={key.id} style={{ transition: 'background 0.1s' }}
                      onMouseEnter={(e) => e.currentTarget.style.background = '#fafaf9'}
                      onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
                    <td style={tdStyle}>
                      <div style={{ fontWeight: 600, color: S[900], marginBottom: 2 }}>{key.name}</div>
                      {key.description && (
                        <div style={{ fontSize: 11, color: S[400], maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {key.description}
                        </div>
                      )}
                    </td>
                    <td style={tdStyle}>
                      <span style={monoStyle}>{key.keyPrefix || '—'}</span>
                    </td>
                    <td style={tdStyle}>
                      <span style={{ fontSize: 12, fontWeight: 500, color: S[600] }}>
                        {key.keyType === 'SDK' ? 'SDK'
                          : key.keyType === 'WORKER' ? 'Worker'
                          : 'Gateway'}
                      </span>
                    </td>
                    <td style={tdStyle}><StatusBadge status={STATUS_KEY_MAP[key.status] || 'expired'} /></td>
                    <td style={tdStyle}>
                      <span style={{ fontSize: 12, color: S[600] }}>
                        {key.rateLimit || 60} RPM
                      </span>
                    </td>
                    <td style={tdStyle}>
                      <span style={{ fontSize: 12, color: S[600] }}>
                        {fmtNumber(key.totalRequests)} req
                      </span>
                    </td>
                    <td style={tdStyle}>
                      <span style={{ fontSize: 12, color: key.expiresAt ? S[600] : S[400] }}>
                        {fmtDate(key.expiresAt)}
                      </span>
                    </td>
                    <td style={tdStyle}>
                      <span style={{ fontSize: 12, color: S[400] }}>{fmtDate(key.createdAt)}</span>
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>
                      <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                        {key.status === 'ACTIVE' && (
                          <>
                            <button className="gov-action-btn" title="Rotate" onClick={() => openRotate(key)} style={{
                              ...btnBase, padding: '5px 8px', fontSize: 11, background: S[100], color: S[600], borderRadius: 6,
                            }}>
                              <RotateCw size={12} />
                            </button>
                            <button className="gov-action-btn" title="Suspend" onClick={() => openSuspend(key)} style={{
                              ...btnBase, padding: '5px 8px', fontSize: 11, background: S[100], color: S[600], borderRadius: 6,
                            }}>
                              <Pause size={12} />
                            </button>
                            <button className="gov-action-btn" title="Update Scope" onClick={() => openScope(key)} style={{
                              ...btnBase, padding: '5px 8px', fontSize: 11, background: S[100], color: S[600], borderRadius: 6,
                            }}>
                              <Shield size={12} />
                            </button>
                          </>
                        )}
                        {key.status === 'SUSPENDED' && (
                          <button className="gov-action-btn" title="Reactivate" onClick={() => handleReactivate(key)} style={{
                            ...btnBase, padding: '5px 8px', fontSize: 11, background: ACCENT.emerald.bg, color: ACCENT.emerald.icon, borderRadius: 6,
                          }}>
                            <Play size={12} />
                          </button>
                        )}
                        {(key.status === 'ACTIVE' || key.status === 'ROTATING' || key.status === 'SUSPENDED') && (
                          <button title="Revoke" onClick={() => openRevoke(key)} style={{
                            ...btnBase, padding: '5px 8px', fontSize: 11, background: ACCENT.rose.bg, color: ACCENT.rose.icon, borderRadius: 6,
                          }}>
                            <ShieldOff size={12} />
                          </button>
                        )}
                        <button className="gov-action-btn" title="Audit Log" onClick={() => handleViewAudit(key)} style={{
                          ...btnBase, padding: '5px 8px', fontSize: 11, background: S[100], color: S[600], borderRadius: 6,
                        }}>
                          <FileText size={12} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {filteredKeys.length > pageSize && (
          <div style={{ marginTop: 16 }}>
            <Pagination
              totalItems={filteredKeys.length}
              page={page}
              pageSize={pageSize}
              onPageChange={setPage}
            />
          </div>
        )}
      </Card>

      {/* ── Create Modal ──────────────────────────────────────────────────── */}
      <Modal
        isOpen={isCreateModalOpen}
        title="Create Gateway API Key"
        onClose={() => setIsCreateModalOpen(false)}
        wide
        actions={
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <GhostButton onClick={() => setIsCreateModalOpen(false)}>Cancel</GhostButton>
            <PrimaryButton icon={Key} onClick={handleCreate} disabled={!createForm.name.trim()}>
              Create Key
            </PrimaryButton>
          </div>
        }
      >
        <div style={formRowStyle}>
          <div style={formGroupStyle}>
            <label style={labelStyle}>Key Name *</label>
            <input
              style={{ ...inputBase, ...(createFormErrors.name ? { borderColor: '#EF4444', background: '#FEF2F2' } : {}) }}
              placeholder="e.g. Production Gateway Key"
              value={createForm.name}
              onChange={(e) => { setCreateForm((f) => ({ ...f, name: e.target.value })); setCreateFormErrors((prev) => ({ ...prev, name: undefined })); }}
            />
            {createFormErrors.name && (
              <div style={{ color: '#DC2626', fontSize: 11, marginTop: 4, fontWeight: 500 }}>{createFormErrors.name}</div>
            )}
          </div>
          <div style={formGroupStyle}>
            <label style={labelStyle}>Key Type</label>
            <select
              style={{ ...selectStyle, width: '100%' }}
              value={createForm.keyType}
              onChange={(e) => setCreateForm((f) => ({ ...f, keyType: e.target.value }))}
            >
              <option value="GATEWAY">Gateway — Full Proxy Invocation</option>
              <option value="SDK">SDK — Split Pre/Post Check</option>
              <option value="WORKER">Worker — Conductor SDK polling</option>
            </select>
          </div>
        </div>

        <div style={formGroupStyle}>
          <label style={labelStyle}>Description</label>
          <input
            style={inputBase}
            placeholder="Optional description for this key"
            value={createForm.description}
            onChange={(e) => setCreateForm((f) => ({ ...f, description: e.target.value }))}
          />
        </div>

        <div style={formRowStyle}>
          <div style={formGroupStyle}>
            <label style={labelStyle}>Expires In (days)</label>
            <input
              type="number"
              style={inputBase}
              value={createForm.expiresInDays}
              min={1}
              max={365}
              onChange={(e) => setCreateForm((f) => ({ ...f, expiresInDays: e.target.value }))}
            />
          </div>
          <div style={formGroupStyle}>
            <label style={labelStyle}>Permissions</label>
            <input
              style={inputBase}
              placeholder="EXECUTE,READ,AUDIT"
              value={createForm.permissions}
              onChange={(e) => setCreateForm((f) => ({ ...f, permissions: e.target.value }))}
            />
            <div style={{ fontSize: 11, color: S[500], marginTop: 4 }}>
              Comma-separated: EXECUTE, READ, AUDIT
            </div>
          </div>
        </div>

        <div style={formRowStyle}>
          <div style={formGroupStyle}>
            <label style={labelStyle}>Rate Limit (RPM)</label>
            <input
              type="number"
              style={inputBase}
              value={createForm.rateLimit}
              min={1}
              onChange={(e) => setCreateForm((f) => ({ ...f, rateLimit: e.target.value }))}
            />
          </div>
          <div style={formGroupStyle}>
            <label style={labelStyle}>Burst Limit (RPS)</label>
            <input
              type="number"
              style={inputBase}
              value={createForm.burstLimit}
              min={1}
              onChange={(e) => setCreateForm((f) => ({ ...f, burstLimit: e.target.value }))}
            />
          </div>
        </div>

        <div style={formGroupStyle}>
          <label style={labelStyle}>IP Allowlist (one per line, CIDR supported)</label>
          <textarea
            style={{ ...inputBase, fontFamily: "'IBM Plex Mono', monospace", minHeight: 80, resize: 'vertical', fontSize: 12 }}
            placeholder={"192.168.1.0/24\n10.0.0.1\n0.0.0.0/0 (allow all)"}
            value={createForm.ipAllowlist}
            onChange={(e) => setCreateForm((f) => ({ ...f, ipAllowlist: e.target.value }))}
          />
        </div>

        {/* Agent scope — Gateway/SDK keys. Pickers replace the legacy
            paste-UUID-by-hand textarea so users select agents by name
            and we store UUIDs internally. Empty selection = the key
            works against every agent in the tenant. */}
        {createForm.keyType !== 'WORKER' && (
          <div style={formGroupStyle}>
            <label style={labelStyle}>Scoped Agents (none selected = all agents in tenant)</label>
            <MultiSelectPicker
              items={agentList}
              selectedIds={createForm.scopedAgentIds}
              onChange={(ids) => {
                setCreateForm((f) => ({ ...f, scopedAgentIds: ids }));
                setCreateFormErrors((prev) => ({ ...prev, scopedAgentIds: undefined }));
              }}
              placeholder="Search agents by name…"
              emptyHint="No agents in this tenant yet."
            />
            {createFormErrors.scopedAgentIds && (
              <div style={{
                color: '#DC2626', fontSize: 11, marginTop: 4, fontWeight: 500,
                background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 6,
                padding: '8px 10px', lineHeight: 1.4,
              }}>
                {createFormErrors.scopedAgentIds}
              </div>
            )}
          </div>
        )}

        {/* Workflow scope — WORKER keys only. Same picker, sourced
            from /api/v1/workflows. Empty = tenant-wide. */}
        {createForm.keyType === 'WORKER' && (
          <div style={formGroupStyle}>
            <label style={labelStyle}>Scoped Workflows (none selected = all workflows in tenant)</label>
            <MultiSelectPicker
              items={workflowList}
              selectedIds={createForm.scopedWorkflowIds}
              onChange={(ids) => {
                setCreateForm((f) => ({ ...f, scopedWorkflowIds: ids }));
                setCreateFormErrors((prev) => ({ ...prev, scopedWorkflowIds: undefined }));
              }}
              placeholder="Search workflows by name…"
              emptyHint="No workflows in this tenant yet."
            />
            {createFormErrors.scopedWorkflowIds && (
              <div style={{
                color: '#DC2626', fontSize: 11, marginTop: 4, fontWeight: 500,
                background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 6,
                padding: '8px 10px', lineHeight: 1.4,
              }}>
                {createFormErrors.scopedWorkflowIds}
              </div>
            )}
            <div style={{ fontSize: 10, color: '#78716C', marginTop: 3 }}>
              Workers polling against this key will only see tasks belonging to
              the selected workflows. Leave empty for tenant-wide.
            </div>
          </div>
        )}
      </Modal>

      {/* ── Rotate Modal ──────────────────────────────────────────────────── */}
      <Modal
        isOpen={isRotateModalOpen}
        title={`Rotate Key: ${targetKey?.name || ''}`}
        onClose={() => setIsRotateModalOpen(false)}
        actions={
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <GhostButton onClick={() => setIsRotateModalOpen(false)}>Cancel</GhostButton>
            <button onClick={handleRotate} style={{
              ...btnBase, padding: '8px 14px', fontSize: 12, fontWeight: 600,
              background: ACCENT.amber.icon, color: '#fff', borderRadius: 8,
            }}>
              <RotateCw size={13} /> Rotate Key
            </button>
          </div>
        }
      >
        <p style={{ fontSize: 13, color: S[600], marginBottom: 16 }}>
          A new key will be generated. The current key will continue working during the grace period,
          after which it will be automatically expired.
        </p>
        <div style={formGroupStyle}>
          <label style={labelStyle}>Grace Period (hours)</label>
          <input
            type="number"
            style={inputBase}
            value={rotateGraceHours}
            min={1}
            max={720}
            onChange={(e) => setRotateGraceHours(e.target.value)}
          />
          <div style={{ fontSize: 11, color: S[500], marginTop: 4 }}>
            Default: 24 hours. The old key remains valid during this window.
          </div>
        </div>
      </Modal>

      {/* ── Revoke Modal ──────────────────────────────────────────────────── */}
      <Modal
        isOpen={isRevokeModalOpen}
        title={`Revoke Key: ${targetKey?.name || ''}`}
        onClose={() => setIsRevokeModalOpen(false)}
        actions={
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <GhostButton onClick={() => setIsRevokeModalOpen(false)}>Cancel</GhostButton>
            <button onClick={handleRevoke} style={{
              ...btnBase, padding: '8px 14px', fontSize: 12, fontWeight: 600,
              background: ACCENT.rose.icon, color: '#fff', borderRadius: 8,
            }}>
              <ShieldOff size={13} /> Revoke Permanently
            </button>
          </div>
        }
      >
        <div style={{
          background: ACCENT.rose.bg, border: `1px solid ${ACCENT.rose.border}`, borderRadius: 8, padding: 12, marginBottom: 16,
          display: 'flex', gap: 8, alignItems: 'flex-start',
        }}>
          <AlertTriangle size={16} color={ACCENT.rose.icon} style={{ flexShrink: 0, marginTop: 2 }} />
          <span style={{ fontSize: 13, color: ACCENT.rose.text }}>
            This action is permanent and cannot be undone. The key will immediately stop working.
          </span>
        </div>
        <div style={formGroupStyle}>
          <label style={labelStyle}>Reason (optional)</label>
          <input
            style={inputBase}
            placeholder="e.g. Key compromised, no longer needed"
            value={actionReason}
            onChange={(e) => setActionReason(e.target.value)}
          />
        </div>
      </Modal>

      {/* ── Suspend Modal ─────────────────────────────────────────────────── */}
      <Modal
        isOpen={isSuspendModalOpen}
        title={`Suspend Key: ${targetKey?.name || ''}`}
        onClose={() => setIsSuspendModalOpen(false)}
        actions={
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <GhostButton onClick={() => setIsSuspendModalOpen(false)}>Cancel</GhostButton>
            <button onClick={handleSuspend} style={{
              ...btnBase, padding: '8px 14px', fontSize: 12, fontWeight: 600,
              background: ACCENT.amber.icon, color: '#fff', borderRadius: 8,
            }}>
              <Pause size={13} /> Suspend Key
            </button>
          </div>
        }
      >
        <p style={{ fontSize: 13, color: S[600], marginBottom: 16 }}>
          Suspending will temporarily disable this key. It can be reactivated later.
        </p>
        <div style={formGroupStyle}>
          <label style={labelStyle}>Reason (optional)</label>
          <input
            style={inputBase}
            placeholder="e.g. Investigating anomalous traffic"
            value={actionReason}
            onChange={(e) => setActionReason(e.target.value)}
          />
        </div>
      </Modal>

      {/* ── Update Scope Modal ────────────────────────────────────────────── */}
      <Modal
        isOpen={isScopeModalOpen}
        title={`Update Scope: ${targetKey?.name || ''}`}
        onClose={() => setIsScopeModalOpen(false)}
        actions={
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <GhostButton onClick={() => setIsScopeModalOpen(false)}>Cancel</GhostButton>
            <PrimaryButton icon={Shield} onClick={handleUpdateScope}>
              Update Scope
            </PrimaryButton>
          </div>
        }
      >
        <p style={{ fontSize: 13, color: S[600], marginBottom: 16 }}>
          Restrict this key to specific agents. Leave empty to allow access to all agents.
        </p>
        <div style={formGroupStyle}>
          <label style={labelStyle}>Agent IDs (one UUID per line)</label>
          <textarea
            style={{
              ...inputBase,
              fontFamily: "'IBM Plex Mono', monospace",
              minHeight: 120,
              resize: 'vertical',
              fontSize: 12,
              ...(scopeFormErrors.scopedAgentIds ? { borderColor: '#EF4444', background: '#FEF2F2' } : {}),
            }}
            placeholder="Leave empty to allow all agents"
            value={scopeForm}
            onChange={(e) => { setScopeForm(e.target.value); setScopeFormErrors({}); }}
          />
          {scopeFormErrors.scopedAgentIds && (
            <div style={{ color: '#DC2626', fontSize: 11, marginTop: 6, fontWeight: 500, lineHeight: '1.4' }}>
              {scopeFormErrors.scopedAgentIds}
            </div>
          )}
          <div style={{ fontSize: 11, color: S[400], marginTop: 6 }}>
            Tip: Open Agent Details for any agent and click <strong>Copy</strong> on the Agent UUID field.
          </div>
        </div>
      </Modal>

      {/* ── Audit Log Modal ───────────────────────────────────────────────── */}
      <Modal
        isOpen={isAuditModalOpen}
        title={`Audit Log: ${targetKey?.name || ''}`}
        onClose={() => { setIsAuditModalOpen(false); setAuditLogs([]); }}
        wide
      >
        {auditLoading ? (
          <div style={{ textAlign: 'center', padding: 32, color: S[500] }}>Loading audit trail...</div>
        ) : auditLogs.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 32, color: S[500] }}>No audit events recorded yet.</div>
        ) : (
          <div style={{ maxHeight: 400, overflowY: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={thStyle}>Event</th>
                  <th style={thStyle}>Actor</th>
                  <th style={thStyle}>Details</th>
                  <th style={thStyle}>Timestamp</th>
                </tr>
              </thead>
              <tbody>
                {auditLogs.map((log, i) => (
                  <tr key={log.id || i}>
                    <td style={tdStyle}>
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', padding: '2px 8px', borderRadius: 4,
                        fontSize: 11, fontWeight: 600, background: S[100], color: S[600],
                      }}>
                        {log.eventType}
                      </span>
                    </td>
                    <td style={{ ...tdStyle, fontSize: 12, color: S[500] }}>
                      {log.actorId ? log.actorId.substring(0, 8) + '...' : 'System'}
                    </td>
                    <td style={{ ...tdStyle, fontSize: 12, color: S[500], maxWidth: 300 }}>
                      {log.reason || log.details || '—'}
                    </td>
                    <td style={{ ...tdStyle, fontSize: 12, color: S[400] }}>
                      {fmtDate(log.createdAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Modal>
    </V2PageShell>
  );
}
